import { Component, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';
import { loadStripe, Stripe, StripeCardCvcElement, StripeCardExpiryElement, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js';
import { httpsCallable } from 'firebase/functions';
import { environment } from '../../tambr.config';
import { FirebaseService } from '../../utils/firebase';
import { addStyleSettings } from '../../utils/custom-style';

import { WidgetPaymentMethodSaveRequest } from '@artask/tambr-models/models/widget';
import { CustomerGetClientSecretResponse } from '@artask/tambr-models/models/customer.model';



@Component({
  tag: 'tambr-payment-method-add',
  styleUrl: 'tambr-payment-method-add.css',
  shadow: false,
})
export class TambrAccountPaymentMethodAdd {

  @Prop() actionLoading: boolean = false;

  @Event() tambrPaymentMethodAddSuccess: EventEmitter;
  @Event() tambrPaymentMethodAddClose: EventEmitter;

  stripe: Stripe;
  elements: StripeElements;
  clientSecret: string;

  @State() cardNumberElement: StripeCardNumberElement;
  @State() cardExpiryElement: StripeCardExpiryElement;
  @State() cardCvcElement: StripeCardCvcElement;

  @State() cardNumberElementMounted: boolean;
  @State() cardExpiryElementMounted: boolean;
  @State() cardCvcElementMounted: boolean;

  @State() cardNumberComplete: boolean = false;
  @State() cardExpiryComplete: boolean = false;
  @State() cardCvcComplete: boolean = false;

  @State() clientSecretLoading: boolean = false;
  @State() submitLoading: boolean = false;
  @State() error: string | null = null;

  @Listen('click') onClickEvent( ev: MouseEvent ) {
    const targets = ev.composedPath() as EventTarget[];
    if (targets[0] instanceof Element && targets[0].id === 'tambr-payment-method-add') return this.close();
  }

  async connectedCallback() {
    if (this.elements) this.mountElements();

    this.clientSecretLoading = true;
    try {
      const getSetupIntentClientSecret = httpsCallable< null, CustomerGetClientSecretResponse >
        ( FirebaseService.functions, 'getSetupIntentClientSecret' );

      const response = await getSetupIntentClientSecret();
      this.clientSecret = response.data.clientSecret;
    } catch(e) {
      this.error = e.message;
    }
    this.clientSecretLoading = false;
    console.log(this.clientSecret)
  }

  async componentDidLoad() {
    this.stripe = await loadStripe(environment.stripePlatformId, { apiVersion: '2022-11-15' });
    this.elements = this.stripe.elements();

    this.cardNumberElement = this.elements.create('cardNumber');
    this.cardExpiryElement = this.elements.create('cardExpiry');
    this.cardCvcElement = this.elements.create('cardCvc');

    this.cardNumberElement.on('ready', () => this.cardNumberElementMounted = true );
    this.cardExpiryElement.on('ready', () => this.cardExpiryElementMounted = true );
    this.cardCvcElement.on('ready', () => this.cardCvcElementMounted = true );

    this.cardNumberElement.on( 'change', event => this.cardNumberComplete = event.complete );
    this.cardExpiryElement.on( 'change', event => this.cardExpiryComplete = event.complete );
    this.cardCvcElement.on( 'change', event => this.cardCvcComplete = event.complete );

    this.mountElements();
  }

  mountElements() {
    this.cardNumberElement.mount('#tambr-payment-method-add-setup-form-card-number-element');
    this.cardExpiryElement.mount('#tambr-payment-method-add-setup-form-card-expiry-element');
    this.cardCvcElement.mount('#tambr-payment-method-add-setup-form-card-cvc-element');
  }

  disableElements() {
    this.cardNumberElement.update({ disabled: true });
    this.cardCvcElement.update({ disabled: true });
    this.cardExpiryElement.update({ disabled: true });
  }

  enableElements() {
    this.cardNumberElement.update({ disabled: false });
    this.cardCvcElement.update({ disabled: false });
    this.cardExpiryElement.update({ disabled: false });
  }

  disconnectedCallback() {
    this.cardNumberElement.unmount();
    this.cardExpiryElement.unmount();
    this.cardCvcElement.unmount();

    this.cardNumberElementMounted = false;
    this.cardExpiryElementMounted = false;
    this.cardCvcElementMounted = false;

    this.cardNumberComplete = false;
    this.cardExpiryComplete = false;
    this.cardCvcComplete = false;
  }

  close() { 
    if (this.submitLoading || this.actionLoading) return;
    this.tambrPaymentMethodAddClose.emit();
  }

  async handleSubmit(ev: any) {
    ev.preventDefault();
    if (this.submitLoading || this.actionLoading) return;

    this.error = null;
    this.submitLoading = true;
    this.disableElements();
    
    try {
      const paymentMethodResult = await this.stripe.createPaymentMethod({ type: 'card', card: this.cardNumberElement });
      if ( paymentMethodResult.error ) throw new Error( paymentMethodResult.error.message );

      const paymentMethodId = paymentMethodResult.paymentMethod.id;
      const { brand, last4, exp_month: expMonth, exp_year: expYear } = paymentMethodResult.paymentMethod.card;

      const cardSetupResult = await this.stripe.confirmCardSetup( this.clientSecret, { payment_method: paymentMethodId } );
      if ( cardSetupResult.error)  throw new Error( cardSetupResult.error.message );

      const newPaymentMethod: WidgetPaymentMethodSaveRequest = {
        paymentMethodId,
        brand,
        last4,
        expMonth,
        expYear,
        makeDefault: true
      }

      const paymentMethodSave = httpsCallable<WidgetPaymentMethodSaveRequest>( FirebaseService.functions, 'widget_paymentMethodSave' );
      await paymentMethodSave( newPaymentMethod );

      this.tambrPaymentMethodAddSuccess.emit( paymentMethodId );
              
    } catch(e) {
      console.error(e);
      this.error = e.message;      
    }
    this.submitLoading = false;
    this.enableElements();  
  }

  isButtonDisabled() {
    return this.submitLoading 
      || this.actionLoading
      || this.clientSecretLoading 
      || !this.cardNumberElementMounted 
      || !this.cardExpiryElementMounted 
      || !this.cardCvcElementMounted
      || !this.cardNumberComplete
      || !this.cardExpiryComplete
      || !this.cardCvcComplete
  }

  render() {
    return (
      <Host style={ addStyleSettings() }>
        <div id="tambr-payment-method-add" class="tambr-overlay">
          <div class='tambr-modal-window'>
            
            <div class="tambr-modal-header">
              <tambr-icon type="close" class="close-button" onClick={ () => this.close() } />
            </div>

            <form id="tambr-payment-method-add-setup-form" onSubmit={ (ev) => this.handleSubmit(ev) }>
              
              <div id="tambr-payment-method-add-setup-form-elements-wrapper">
                <div id="tambr-payment-method-add-setup-form-card-number-element" />
                <div class="card-info">
                  <div id="tambr-payment-method-add-setup-form-card-expiry-element" />
                  <div id="tambr-payment-method-add-setup-form-card-cvc-element" />
                </div>
                { this.renderFormPreloader() }
              </div>

              <div class="tambr-payment-method-add-error">{ this.error }</div>

              <div class="tambr-action-buttons">
                <button id="card-button" type="submit" disabled={ this.isButtonDisabled() }
                  class={ 'tambr-button' + ( (this.submitLoading || this.actionLoading) ? ' loading' : '' ) }>
                  <div class="tambr-button-loading"><div>Loading...</div></div>
                  <span>Save Card</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      </Host>
    );
  }

  renderFormPreloader() {
    const preloaderVisible = !this.cardNumberElementMounted || !this.cardExpiryElementMounted  || !this.cardCvcElementMounted;
    return (
      <div id="tambr-payment-method-add-setup-form-elements-preloader" class={ preloaderVisible ? 'visible' : '' }>
        <span class="skeleton-text" />
        <div class="card-info">
          <span class="skeleton-text" />
          <span class="skeleton-text" />
        </div>
      </div>
    );
  }

}
